import fallImage from '$src/assets/images/fall.png'
import springImage from '$src/assets/images/spring.png'
import summerImage from '$src/assets/images/summer.png'
import winterImage from '$src/assets/images/winter.png'
import { AttachmentResponse, MaintenanceTaskWithSteps } from '@markku/types'
import { partition, sortBy } from '@markku/utils'
import dayjs from 'dayjs'

// Type definitions
export interface MaintenanceTask {
  id: string
  title: string
  completed: boolean
  isUserTask: boolean
  isSystemTask: boolean
  createdByUserId: number | null
  category?: string
  description?: string
  deadline?: string
  attachments?: AttachmentResponse[]
  instructions?: string[]
  lastCompleted?: string
  nextScheduled?: string
  estimatedDuration?: string
}

export interface SeasonData {
  season: string
  finnishName: string
  year: number
  tasks: MaintenanceTask[]
  recommendedTasks: MaintenanceTask[]
  markkuTasks: MaintenanceTask[]
  completedCount: number
  totalCount: number
  monthRange: string
  backgroundImage: string
  backgroundColor: string
  totalCost: number
}

export const SEASON_ORDER = ['spring', 'summer', 'fall', 'winter'] as const
export type Season = (typeof SEASON_ORDER)[number]

export interface TargetSeason {
  season: string
  year: number
}

// Season helper functions
export const getSeasonName = (season: string): string => {
  const names: Record<string, string> = {
    spring: 'Kevään tehtävät',
    summer: 'Kesän tehtävät',
    fall: 'Syksyn tehtävät',
    winter: 'Talven tehtävät',
  }
  return names[season] || season
}

export const getMonthRange = (season: string): string => {
  const ranges: Record<string, string> = {
    spring: 'Maalis-Toukokuu',
    summer: 'Kesä-Elokuu',
    fall: 'Syys-Marraskuu',
    winter: 'Joulu-Helmikuu',
  }
  return ranges[season] || ''
}

export const getFormattedDateRange = (season: string, year: number): string => {
  const monthRange = getMonthRange(season)
  if (season === 'winter') {
    return `${monthRange} ${year}-${year + 1}`
  }
  return `${monthRange} ${year}`
}

export const getSeasonImage = (season: string): string => {
  const images: Record<string, string> = {
    spring: springImage,
    summer: summerImage,
    fall: fallImage,
    winter: winterImage,
  }
  return images[season] || springImage
}

export const getSeasonColor = (season: string): string => {
  const colors: Record<string, string> = {
    spring: '#E8F5E9',
    summer: '#FFF9C4',
    fall: '#FFE0B2',
    winter: '#E3F2FD',
  }
  return colors[season] || '#F5F5F5'
}

export const mapSchedulingTypeToSeason = (
  schedulingType: string | null,
): string => {
  if (!schedulingType) return 'spring'

  const mapping: Record<string, string> = {
    seasonal_spring: 'spring',
    seasonal_summer: 'summer',
    seasonal_fall: 'fall',
    seasonal_winter: 'winter',
  }

  return mapping[schedulingType] || 'spring'
}

export const getSeasonAndYearFromDeadline = (
  deadline: string,
): { season: string; year: number } => {
  const date = new Date(deadline)
  const month = date.getMonth() + 1
  const year = date.getFullYear()

  if (month >= 3 && month <= 5) return { season: 'spring', year }
  if (month >= 6 && month <= 8) return { season: 'summer', year }
  if (month >= 9 && month <= 11) return { season: 'fall', year }
  // Winter spans Dec-Feb: Dec belongs to current year, Jan/Feb to previous year's winter
  if (month === 12) return { season: 'winter', year }
  return { season: 'winter', year: year - 1 }
}

export const getSeasonEndDate = (season: string, year: number): string => {
  const endMonths: Record<string, number> = {
    spring: 5, // May
    summer: 8, // August
    fall: 11, // November
    winter: 2, // February (next year)
  }

  const month = endMonths[season]
  if (!month) return dayjs().format('YYYY-MM-DD')

  const targetYear = season === 'winter' ? year + 1 : year
  return dayjs()
    .year(targetYear)
    .month(month - 1)
    .endOf('month')
    .format('YYYY-MM-DD')
}

export const getCurrentSeasonIndex = (): number => {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 0 // spring
  if (month >= 5 && month <= 7) return 1 // summer
  if (month >= 8 && month <= 10) return 2 // fall
  return 3 // winter
}

export const getCurrentSeason = (): string => {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 'spring'
  if (month >= 5 && month <= 7) return 'summer'
  if (month >= 8 && month <= 10) return 'fall'
  return 'winter'
}

export const getTaskSeason = (task: {
  propertyId: number | null
  deadline: string | null
  schedulingType: string | null
}): string => {
  if (task.propertyId && task.deadline) {
    return getSeasonAndYearFromDeadline(task.deadline).season
  }
  return mapSchedulingTypeToSeason(task.schedulingType)
}

// Transform backend task to UI format
export const mapTaskToUI = (
  task: MaintenanceTaskWithSteps,
  completedTaskIds: Set<number>,
  isUserManaged: boolean,
): MaintenanceTask => ({
  id: task.id.toString(),
  title: task.name,
  completed: completedTaskIds.has(task.id),
  isUserTask: isUserManaged,
  isSystemTask: task.propertyId === null,
  createdByUserId: task.createdByUserId || null,
  category: task.category || undefined,
  description: task.description || undefined,
  deadline: task.deadline || undefined,
  attachments: task.attachments || undefined,
  estimatedDuration: task.estimatedDurationMinutes
    ? `${Math.round(task.estimatedDurationMinutes / 60)} tuntia`
    : undefined,
})

// Order tasks: incomplete first (sorted), then complete (sorted)
export const orderTasksByCompletion = <T extends { id: number }>(
  tasks: T[],
  completedIds: Set<number>,
  sortFn: (t: T) => string | number | Date,
): T[] => {
  const [incomplete, complete] = partition(
    tasks,
    (t) => !completedIds.has(t.id),
  )
  return [...sortBy(incomplete, sortFn), ...sortBy(complete, sortFn)]
}
